import React, { useEffect, useRef, useState } from 'react';

import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Collapsible } from '@buildoutinc/blueprint-react/ui/Collapsible';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faChevronDown, faChevronRight, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { Button } from 'components/ui/button';
import { showSnackbar } from 'components/ui/snackbar/helper';
import { post, put } from 'helpers/api';
import { serverErrors } from 'helpers/api_errors';
import { moveHeadline, moveReason, viewLabel } from 'bundles/os/home/reasons';
import { latestRecommendations, userTurns } from '../../hooks/use_next_actions';
import { announceNextAction, NEXT_ACTION_CHANGED_EVENT } from '../../events';
import { useConversationContext } from '../../conversation_context';
import { useNextActionsContext } from '../../next_actions_context';

import type { NextActionChange } from '../../events';
import type { DoneMove, NextActionQueue } from '../../hooks/use_next_actions';
import type { Recommendation } from '../types';

interface RecommendationCarouselProps {
  queue: NextActionQueue;
}

interface CompletedMovesProps {
  moves: DoneMove[];
}

export function PinnedRecommendations() {
  const { messages } = useConversationContext();
  useClearsFromHome();

  const latest = latestRecommendations(messages);
  if (!latest) {
    return null;
  }

  return <RecommendationCarousel key={latest.id} queue={latest} />;
}

function useClearsFromHome() {
  const { messages, updateNextActions } = useConversationContext();
  const latest = useRef({ messages, updateNextActions });
  latest.current = { messages, updateNextActions };

  useEffect(() => {
    function changed(event: Event) {
      const { as, source, subjectId, subjectType } = (event as CustomEvent<NextActionChange>).detail;
      const queue = latestRecommendations(latest.current.messages);
      const match = queue?.recommendations.find(recommendation => (
        recommendation.subjectType === subjectType && String(recommendation.subjectId) === String(subjectId)
      ));
      const was = match && queue?.cleared.find(move => move.position === match.position);
      if (source === 'assistant' || !queue || !match || (as ? was?.as === as : !was)) {
        return;
      }

      const turn = userTurns(latest.current.messages);

      latest.current.updateNextActions(queue, (progress) => {
        const rest = progress.cleared.filter(move => move.position !== match.position);
        return { ...progress, cleared: as ? [...rest, { as, position: match.position, turn }] : rest };
      });
    }

    window.addEventListener(NEXT_ACTION_CHANGED_EVENT, changed);
    return () => window.removeEventListener(NEXT_ACTION_CHANGED_EVENT, changed);
  }, []);
}

function completionFailure(recommendation: Recommendation, thrown: unknown) {
  const [reason] = serverErrors(thrown);
  const refusal = `Couldn't complete "${recommendation.title || moveHeadline(recommendation)}"`;

  return typeof reason === 'string' ? `${refusal}: ${reason}. Open it to finish.` : refusal;
}

function RecommendationCarousel({ queue }: RecommendationCarouselProps) {
  const { messages, updateNextActions } = useConversationContext();
  const nextActions = useNextActionsContext();
  const [completing, setCompleting] = useState(false);

  const { index, marked, open, recommendation, remaining } = nextActions.view(queue, messages);
  if (queue.dismissed) {
    return null;
  }

  async function markDone(recommendation: Recommendation) {
    const { completePath, subjectId, subjectType } = recommendation;

    setCompleting(true);
    try {
      if (completePath) {
        await put(completePath, { completed: true });
      } else if (queue.snoozePath) {
        await post(queue.snoozePath, { subjectId, subjectType });
      }
    } catch (thrown) {
      showSnackbar({ text: completionFailure(recommendation, thrown), variant: 'danger' });
      return;
    } finally {
      setCompleting(false);
    }

    if (completePath) {
      showSnackbar({ text: `Completed "${recommendation.title || moveHeadline(recommendation)}"`, variant: 'success' });
    }

    const move = { as: 'done' as const, position: recommendation.position, turn: userTurns(messages) };
    nextActions.browse(queue, { position: undefined });
    updateNextActions(queue, progress => ({ ...progress, cleared: [...progress.cleared, move] }));
    announceNextAction({ as: 'done', source: 'assistant', subjectId, subjectType });
  }

  return (
    <Collapsible
      className="ai-assistant-pinned flex-shrink-0 px-3 py-3"
      open={open}
      onOpenChange={next => nextActions.toggle(queue, messages, next)}
    >
      <Card className="bg-gradient-ai-card border-purple-heart-200 rounded-3 shadow-lg overflow-hidden">
        <Card.Body className="d-flex flex-column gap-2 bg-transparent">
          <div className="d-flex align-items-center gap-2">
            <Collapsible.Trigger
              className="btn btn-link d-flex align-items-center gap-2 p-0 text-purple-heart-700 text-decoration-none"
            >
              <FontAwesomeIcon className="fs-xs" icon={open ? faChevronDown : faChevronRight} />
              <span className="fs-sm fw-semibold text-uppercase">Next actions</span>
              {recommendation && (
                <Badge className="bg-purple-heart-200" variant="secondary">
                  {index + 1} of {remaining.length}
                </Badge>
              )}
            </Collapsible.Trigger>
            <div className="d-flex align-items-center gap-1 ms-auto">
              {recommendation && (
                <>
                  <Button
                    aria-label="Previous recommendation"
                    disabled={completing || !open || remaining.length < 2}
                    icon={faArrowLeft}
                    size="sm"
                    variant="ghost"
                    onClick={() => nextActions.page(queue, -1)}
                  />
                  <Button
                    aria-label="Next recommendation"
                    disabled={completing || !open || remaining.length < 2}
                    icon={faArrowRight}
                    size="sm"
                    variant="ghost"
                    onClick={() => nextActions.page(queue, 1)}
                  />
                </>
              )}
              <Button
                aria-label="Close next actions"
                icon={faXmark}
                size="sm"
                variant="ghost"
                onClick={() => updateNextActions(queue, progress => ({ ...progress, dismissed: true }))}
              />
            </div>
          </div>
          <Collapsible.Content>
            {recommendation ? (
              <div className="d-flex flex-column">
                {marked && <div className="small text-muted fst-italic">Marked done. Next up…</div>}
                <div className="fw-semibold">
                  {recommendation.position === queue.recommendations[0].position && 'Start with '}
                  {moveHeadline(recommendation)}
                </div>
                <div className="text-muted mt-1">{moveReason(recommendation)}</div>
                <div className="d-flex align-items-center gap-2 mt-3">
                  <Button href={recommendation.url} label={viewLabel(recommendation)} size="sm" />
                  <Button
                    disabled={completing}
                    label="Done"
                    size="sm"
                    variant="ghost"
                    onClick={() => markDone(recommendation)}
                  />
                </div>
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <FontAwesomeIcon className="text-success" icon={faCircleCheck} />
                <span className="fw-semibold">That's your day cleared.</span>
              </div>
            )}
          </Collapsible.Content>
        </Card.Body>
      </Card>
    </Collapsible>
  );
}

export function CompletedMoves({ moves }: CompletedMovesProps) {
  return (
    <>
      {moves.map(({ key, recommendation }) => <CompletedMove key={key} recommendation={recommendation} />)}
    </>
  );
}

function CompletedMove({ recommendation }: { recommendation: Recommendation }) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible className="mb-3" open={open} onOpenChange={setOpen}>
      <Collapsible.Trigger
        className="btn btn-link btn-sm d-flex align-items-center gap-2 p-0 text-muted text-decoration-none"
      >
        <FontAwesomeIcon className="text-success" icon={faCircleCheck} />
        Completed next action
        <FontAwesomeIcon className="fs-xs" icon={open ? faChevronDown : faChevronRight} />
      </Collapsible.Trigger>
      <Collapsible.Content>
        <div className="d-flex flex-column align-items-start gap-1 mt-2 small">
          <span className="fw-semibold">{moveHeadline(recommendation)}</span>
          <span className="text-muted">{moveReason(recommendation)}</span>
          <Button
            className="mt-1"
            href={recommendation.url}
            label={viewLabel(recommendation)}
            size="sm"
            variant="outline"
          />
        </div>
      </Collapsible.Content>
    </Collapsible>
  );
}
